/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.auth

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withLink
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.common.Constants
import studio.oddpods.cauce.core.ui.CauceButton
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.auth.generated.resources.Res
import studio.oddpods.cauce.feature.auth.generated.resources.landing_consent_middle
import studio.oddpods.cauce.feature.auth.generated.resources.landing_consent_prefix
import studio.oddpods.cauce.feature.auth.generated.resources.landing_consent_privacy
import studio.oddpods.cauce.feature.auth.generated.resources.landing_consent_suffix
import studio.oddpods.cauce.feature.auth.generated.resources.landing_consent_terms
import studio.oddpods.cauce.feature.auth.generated.resources.landing_continue
import studio.oddpods.cauce.feature.auth.generated.resources.landing_email_label
import studio.oddpods.cauce.feature.auth.generated.resources.landing_headline
import studio.oddpods.cauce.feature.auth.generated.resources.landing_proof_1
import studio.oddpods.cauce.feature.auth.generated.resources.landing_proof_2
import studio.oddpods.cauce.feature.auth.generated.resources.landing_proof_3
import studio.oddpods.cauce.feature.auth.generated.resources.landing_subheadline

@Composable
fun LandingScreen(
    onOtpSent: (email: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: LandingViewModel = koinViewModel(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            is LandingEvent.OtpSent -> onOtpSent(event.email)
        }
    }

    LandingScreenContent(
        state = state,
        onEmailChange = { viewModel.trySendAction(LandingAction.EmailChanged(it)) },
        onContinueClick = { viewModel.trySendAction(LandingAction.ContinueClicked) },
        modifier = modifier,
    )
}

@Composable
internal fun LandingScreenContent(
    state: LandingState,
    onEmailChange: (String) -> Unit,
    onContinueClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    KptScaffold(modifier = modifier) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Column(
                modifier = Modifier
                    .widthIn(max = 480.dp)
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = stringResource(Res.string.landing_headline),
                    style = MaterialTheme.typography.headlineMedium,
                    textAlign = TextAlign.Center,
                )
                Spacer(Modifier.height(12.dp))
                Text(
                    text = stringResource(Res.string.landing_subheadline),
                    style = MaterialTheme.typography.bodyLarge,
                    textAlign = TextAlign.Center,
                )

                Spacer(Modifier.height(24.dp))

                ProofBullet(Res.string.landing_proof_1)
                ProofBullet(Res.string.landing_proof_2)
                ProofBullet(Res.string.landing_proof_3)

                Spacer(Modifier.height(24.dp))

                OutlinedTextField(
                    value = state.email,
                    onValueChange = onEmailChange,
                    label = { Text(stringResource(Res.string.landing_email_label)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                    singleLine = true,
                    isError = state.error != null,
                    modifier = Modifier.fillMaxWidth(),
                )

                if (state.error != null) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = state.error.asString(),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }

                Spacer(Modifier.height(24.dp))

                CauceButton(
                    text = stringResource(Res.string.landing_continue),
                    onClick = onContinueClick,
                    isLoading = state.isLoading,
                    modifier = Modifier.fillMaxWidth(),
                )

                Spacer(Modifier.height(16.dp))

                LegalConsentText(modifier = Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
private fun ProofBullet(text: StringResource) {
    Row(Modifier.fillMaxWidth().padding(vertical = 6.dp)) {
        Text("•  ", style = MaterialTheme.typography.bodyMedium)
        Text(
            text = stringResource(text),
            style = MaterialTheme.typography.bodyMedium,
        )
    }
}

@Composable
private fun LegalConsentText(modifier: Modifier = Modifier) {
    val prefix = stringResource(Res.string.landing_consent_prefix)
    val terms = stringResource(Res.string.landing_consent_terms)
    val middle = stringResource(Res.string.landing_consent_middle)
    val privacy = stringResource(Res.string.landing_consent_privacy)
    val suffix = stringResource(Res.string.landing_consent_suffix)

    val linkStyle = TextLinkStyles(
        style = SpanStyle(color = MaterialTheme.colorScheme.primary),
    )

    val annotatedString = buildAnnotatedString {
        append(prefix)
        withLink(LinkAnnotation.Url(url = Constants.TERMS_AND_CONDITIONS_URL, styles = linkStyle)) {
            append(terms)
        }
        append(middle)
        withLink(LinkAnnotation.Url(url = Constants.PRIVACY_POLICY_URL, styles = linkStyle)) {
            append(privacy)
        }
        append(suffix)
    }

    Text(
        text = annotatedString,
        style = MaterialTheme.typography.bodySmall,
        textAlign = TextAlign.Center,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = modifier,
    )
}
